import {
  AriaAttributes, ChangeEvent, CSSProperties, FC, FocusEvent, KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent, ReactNode, RefObject, useEffect, useId, useLayoutEffect, useMemo,
  useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { components } from 'react-select';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import StyledCheckbox from '../../components/shared/StyledCheckbox/StyledCheckbox';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import { ChevronRightOutlineIcon } from '../../components/svgs';
import { ReactComponent as ErrorIcon } from '../../assets/svg/errorIcon.svg';
import {
  ChecklistItem, ChecklistOption, ChecklistRow, checklistRows, hasMatch, isGroup, isNameTaken, matchesLabel,
  setInTree, setOption,
} from './checklist';
import { chipsThatFit } from './chipsThatFit';
import styles from './bt-checklist-select.module.scss';

// Matches the row height in bt-checklist-select.module.scss; Virtuoso places the rows by it.
const ROW_HEIGHT = 36;
// The panel keeps this gap above the window's bottom edge, and never shrinks below the minimum.
const PANEL_MARGIN = 16;
const MIN_PANEL_HEIGHT = 220;
// Match the chip gap and the "+N" badge's room in bt-checklist-select.module.scss.
const CHIP_GAP = 4;
const BADGE_WIDTH = 40;
// More chips than this never fit side by side in a filter field.
const MEASURED_CHIPS = 10;
const SELECTOR_CHIP_CROSS_SIZE = 14;

const depthStyle = (depth: number) => ({ '--depth': depth } as CSSProperties);

type AccessibleName = Pick<AriaAttributes, 'aria-label' | 'aria-labelledby'>;

type FieldChipsProps = {
  picked: ChecklistOption[],
  placeholder: string,
  onRemove: (value: string) => void,
};

const FieldChips: FC<FieldChipsProps> = ({ picked, placeholder, onRemove }) => {
  const [t] = useTranslation('shared', { keyPrefix: 'checklistSelect' });
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(picked.length);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return undefined;
    const fit = () => {
      // A field not laid out, like one on the tab out of view, keeps what it showed.
      if (!row.clientWidth) return;
      // Fractional widths, since rounded ones can add up to a fit the row does not have.
      const widths = Array.from(measure.children).map(chip => chip.getBoundingClientRect().width);
      setShown(chipsThatFit(widths, picked.length, row.getBoundingClientRect().width, CHIP_GAP, BADGE_WIDTH));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(row);
    // The chips can widen while the row keeps its size, as when a web font arrives.
    observer.observe(measure);
    return () => observer.disconnect();
  }, [picked]);

  const remove = (event: ReactMouseEvent, value: string) => {
    // A press on the field opens it; taking a chip away should not.
    event.stopPropagation();
    onRemove(value);
  };

  if (!picked.length) return <span className={styles.placeholder}>{placeholder}</span>;

  return (
    <div ref={rowRef} className={styles.chips} title={picked.map(chip => chip.label).join('\n')}>
      {picked.slice(0, shown).map(chip => (
        <span key={chip.value} className={styles.chip}>
          <span className={styles.chipLabel}>{chip.label}</span>
          <button
            type="button"
            className={styles.chipRemove}
            aria-label={t('remove', 'Ta bort {{label}}', { label: chip.label })}
            onClick={event => remove(event, chip.value)}
          >
            <components.CrossIcon size={SELECTOR_CHIP_CROSS_SIZE} />
          </button>
        </span>
      ))}
      {shown < picked.length && <span className={styles.more}>+{picked.length - shown}</span>}
      <div ref={measureRef} className={styles.measure} aria-hidden>
        {picked.slice(0, MEASURED_CHIPS).map(chip => (
          <span key={chip.value} className={styles.chip}>
            <span className={styles.chipLabel}>{chip.label}</span>
            <span className={styles.chipRemove}><components.CrossIcon size={SELECTOR_CHIP_CROSS_SIZE} /></span>
          </span>
        ))}
      </div>
    </div>
  );
};

type GroupRow = Extract<ChecklistRow, { kind: 'group' }>;
type OptionRow = Extract<ChecklistRow, { kind: 'option' }>;

type PanelProps = {
  id: string,
  name: AccessibleName,
  compact: boolean,
  searchPlaceholder?: string,
  options: readonly ChecklistItem[],
  value: string[],
  onChange: (values: string[]) => void,
  matchesQuery: (option: ChecklistOption, query: string) => boolean,
  groupsSelectable: boolean,
  expanded: ReadonlySet<string>,
  onToggleExpanded: (group: string) => void,
  searchRef: RefObject<HTMLInputElement>,
  showOnly: ReadonlySet<string> | null,
  onShowOnlyChange: (snapshot: ReadonlySet<string> | null) => void,
  onCreate?: (label: string) => Promise<void> | void,
  creating: boolean,
  onCreatingChange: (creating: boolean) => void,
};

const Panel: FC<PanelProps> = ({
  id, name, compact, searchPlaceholder, options, value, onChange, matchesQuery, groupsSelectable,
  expanded, onToggleExpanded, searchRef, showOnly, onShowOnlyChange, onCreate, creating,
  onCreatingChange,
}) => {
  const [t] = useTranslation('shared', { keyPrefix: 'checklistSelect' });
  const [tSelector] = useTranslation('shared', { keyPrefix: 'selector' });
  const [query, setQuery] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<VirtuosoHandle>(null);
  const searchLabel = searchPlaceholder ?? t('search', 'Sök…');
  const onlySelectedLabel = t('onlySelected', 'Visa bara valda');
  const newName = query.trim();
  const creatable = onCreate !== undefined && newName !== '' && showOnly === null && !isNameTaken(options, newName);

  const rows = useMemo(
    () => checklistRows(options, {
      selected: new Set(value), expanded, query, showOnly, matches: matchesQuery, pickableGroups: groupsSelectable,
    }),
    [options, value, expanded, query, showOnly, matchesQuery, groupsSelectable],
  );

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (panel) {
      // Sized before scrolling into view, so opening low on the page or in a modal scrolls nothing.
      const room = window.innerHeight - panel.getBoundingClientRect().top - PANEL_MARGIN;
      panel.style.setProperty('--room', `${Math.max(MIN_PANEL_HEIGHT, Math.round(room))}px`);
      panel.scrollIntoView({ block: 'nearest' });
    }
    (compact ? panelRef : searchRef).current?.focus({ preventScroll: true });
  }, [compact, searchRef]);

  // Virtuoso keeps its scroll position when its data changes, so a new filter would open mid-list.
  useEffect(() => {
    listRef.current?.scrollToIndex(0);
  }, [query, showOnly]);

  const pickOption = (row: OptionRow, checked: boolean) => onChange(groupsSelectable
    ? setInTree(options, value, row.value, checked)
    : setOption(value, row.value, checked));

  const pickGroup = (row: GroupRow) => onChange(setInTree(options, value, row.value, !row.whole));

  const create = async () => {
    onCreatingChange(true);
    // The row disables itself while it waits, which would leave focus nowhere.
    searchRef.current?.focus();
    try {
      await onCreate?.(newName);
    } finally {
      onCreatingChange(false);
    }
  };

  const pickFirstMatch = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    // The Enter that finishes an input-method word is part of typing; Safari marks it keyCode 229.
    const composing = event.nativeEvent.isComposing || event.keyCode === 229;
    if (event.key !== 'Enter' || composing || !newName) return;
    event.preventDefault();
    const match = rows.find(row => (row.kind === 'option'
      ? !row.checked
      : groupsSelectable && !row.whole && row.foundByName));
    if (match?.kind === 'option') pickOption(match, true);
    else if (match) pickGroup(match);
    // A match already ticked still names something that exists, so only a search finding nothing creates.
    else if (creatable && !creating && !hasMatch(options, newName, matchesQuery)) create();
  };

  const clear = () => {
    onChange([]);
    onShowOnlyChange(null);
    // The button disables itself, which would leave focus nowhere.
    searchRef.current?.focus();
  };

  const renderGroup = (row: GroupRow) => {
    const partlyPicked = row.picked > 0 && !row.whole;
    const content = (
      <>
        {row.collapsible && (
          <span className={`${styles.chevron} ${row.open ? styles.chevronOpen : ''}`}>
            <ChevronRightOutlineIcon />
          </span>
        )}
        <span className={styles.rowText} title={row.label}>{row.label}</span>
        {(row.whole || row.picked > 0) && (
          <span className={styles.count}>
            {row.whole ? t('all', 'Alla') : t('picked', {
              count: row.picked,
              defaultValue_one: '{{count}} vald',
              defaultValue_other: '{{count}} valda',
            })}
          </span>
        )}
      </>
    );
    return (
      <div className={styles.groupRow} style={depthStyle(row.depth)}>
        {groupsSelectable && (
          <StyledCheckbox
            checked={row.whole || row.picked > 0}
            dash={partlyPicked}
            // StyledCheckbox dashes only a checked box, so the click's own state cannot be trusted.
            onChange={() => pickGroup(row)}
            inputProps={{ 'aria-label': row.label }}
            // Only the native state reaches a screen reader; aria-checked is ignored on a real checkbox.
            inputRef={(input: HTMLInputElement | null) => { if (input) input.indeterminate = partlyPicked; }}
          />
        )}
        {row.collapsible ? (
          <button
            type="button"
            className={styles.groupToggle}
            aria-expanded={row.open}
            onClick={() => onToggleExpanded(row.value)}
          >
            {content}
          </button>
        ) : (
          <div className={styles.groupHeading}>{content}</div>
        )}
      </div>
    );
  };

  const renderRow = (row: ChecklistRow) => (row.kind === 'group' ? renderGroup(row) : (
    <label className={styles.option} style={depthStyle(row.depth)}>
      <StyledCheckbox
        checked={row.checked}
        onChange={(_event: ChangeEvent<HTMLInputElement>, checked: boolean) => pickOption(row, checked)}
        inputProps={{ 'aria-label': row.label }}
      />
      <span className={styles.rowText} title={row.label}>{row.label}</span>
    </label>
  ));

  const renderCreateRow = () => {
    const createLabel = tSelector('createLabel', 'Skapa "{{userInput}}"', { userInput: newName });
    return (
      <button type="button" className={styles.create} disabled={creating} onClick={create}>
        <span className={styles.rowText} title={createLabel}>{createLabel}</span>
      </button>
    );
  };

  return (
    <div ref={panelRef} id={id} role="dialog" {...name} tabIndex={-1} className={styles.panel}>
      {!compact && (
        <div className={styles.header}>
          {/* Not type="search": the stencil normalize.css makes it content-box, past the panel. */}
          <input
            ref={searchRef}
            className={styles.search}
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={pickFirstMatch}
            placeholder={searchLabel}
            aria-label={searchLabel}
          />
          <div className={styles.tools}>
            <label className={styles.toggle}>
              <StyledCheckbox
                checked={showOnly !== null}
                // A snapshot, so an option unticked while it is on stays listed to be ticked again.
                onChange={(_event: ChangeEvent<HTMLInputElement>, checked: boolean) =>
                  onShowOnlyChange(checked ? new Set(value) : null)}
                inputProps={{ 'aria-label': onlySelectedLabel }}
              />
              <span>{onlySelectedLabel}</span>
            </label>
            <button
              type="button"
              className={styles.clear}
              // Nothing picked may be a choice whose options never loaded; clearing would undo it.
              disabled={!value.length}
              onClick={clear}
            >
              {t('clear', 'Rensa')}
            </button>
          </div>
        </div>
      )}
      {rows.length > 0 && (
        // Every StyledCheckbox attaches a stylesheet of its own, so only the rows in view mount.
        <Virtuoso
          ref={listRef}
          className={styles.list}
          style={{ height: rows.length * ROW_HEIGHT }}
          data={rows}
          fixedItemHeight={ROW_HEIGHT}
          // Tab only reaches mounted rows, so a few past each edge have to exist already.
          increaseViewportBy={ROW_HEIGHT * 4}
          computeItemKey={(_index, row) => `${row.kind}-${row.value}`}
          itemContent={(_index, row) => renderRow(row)}
        />
      )}
      {creatable ? renderCreateRow() : !rows.length && (
        <p className={styles.empty}>{t('empty', 'Inga alternativ att visa')}</p>
      )}
    </div>
  );
};

type Props = {
  options: readonly ChecklistItem[],
  value: string[],
  onChange: (values: string[]) => void,
  placeholder?: string,
  label?: ReactNode,
  required?: boolean,
  errorMessage?: string,
  compact?: boolean,
  searchPlaceholder?: string,
  matchesQuery?: (option: ChecklistOption, query: string) => boolean,
  groupsSelectable?: boolean,
  defaultExpanded?: string[],
  onCreate?: (label: string) => Promise<void> | void,
};

// With groupsSelectable, a section picked whole is handed over as the section's own value.
// onCreate only hands over the typed name; adding what it creates to value, or reporting a failure, is the parent's.
const BtChecklistSelect: FC<Props> = ({
  options, value, onChange, placeholder: givenPlaceholder, label, required, errorMessage,
  compact = false, searchPlaceholder, matchesQuery = matchesLabel, groupsSelectable = false,
  defaultExpanded = [], onCreate,
}) => {
  const [t] = useTranslation('shared', { keyPrefix: 'checklistSelect' });
  const [tSelector] = useTranslation('shared', { keyPrefix: 'selector' });
  const placeholder = givenPlaceholder ?? tSelector('placeholder', 'Välj');
  const [open, setOpen] = useState(false);
  // Here rather than in the panel so clearing the field switches it off too; each opening starts without it.
  const [showOnly, setShowOnly] = useState<ReadonlySet<string> | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set(defaultExpanded));
  // Here rather than in the panel, which closing unmounts while the create is still pending.
  const [creating, setCreating] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const panelId = useId();
  const labelId = useId();
  const name: AccessibleName = label
    ? { 'aria-labelledby': labelId }
    : { 'aria-label': placeholder };

  const picked = useMemo(() => {
    const labelByValue = new Map<string, string>();
    const collect = (item: ChecklistItem) => {
      labelByValue.set(item.value, item.label);
      if (isGroup(item)) item.options.forEach(collect);
    };
    options.forEach(collect);
    return value.map(pick => ({ value: pick, label: labelByValue.get(pick) ?? pick }));
  }, [options, value]);

  // On the document: a row the virtual list unmounts drops focus to the page, past the root.
  useEffect(() => {
    if (!open) return undefined;
    const closeOnPressOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // The label neither toggles the panel nor takes focus, so a press there counts as outside.
      if (!rootRef.current?.contains(target) || labelRef.current?.contains(target)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      fieldRef.current?.focus();
    };
    // Focus leaving the window has no relatedTarget, so closeWhenFocusLeaves never sees it.
    const closeOnWindowBlur = () => {
      setOpen(false);
      if (rootRef.current?.contains(document.activeElement)) fieldRef.current?.focus();
    };
    document.addEventListener('mousedown', closeOnPressOutside);
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('blur', closeOnWindowBlur);
    return () => {
      document.removeEventListener('mousedown', closeOnPressOutside);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('blur', closeOnWindowBlur);
    };
  }, [open]);

  // A row the virtual list unmounts takes focus with it and leaves no relatedTarget.
  const closeWhenFocusLeaves = (event: FocusEvent) => {
    const next = event.relatedTarget as Node | null;
    if (next && !rootRef.current?.contains(next)) setOpen(false);
  };

  const toggleExpanded = (group: string) => setExpanded(current => {
    const next = new Set(current);
    if (next.has(group)) next.delete(group);
    else next.add(group);
    return next;
  });

  // The pressed button goes with what it removed, so focus stays where the user was working.
  const refocus = () => (open && !compact ? searchRef : fieldRef).current?.focus();

  const removeChip = (removed: string) => {
    onChange(value.filter(pick => pick !== removed));
    refocus();
  };

  const clearAll = (event: ReactMouseEvent) => {
    // A press on the field opens it; clearing it should not.
    event.stopPropagation();
    onChange([]);
    setShowOnly(null);
    refocus();
  };

  const toggleOpen = () => {
    if (!open) setShowOnly(null);
    setOpen(!open);
  };

  const fieldClass = `${styles.field} ${open ? styles.fieldOpen : ''}`;

  return (
    <div ref={rootRef} className={styles.root} onBlur={closeWhenFocusLeaves}>
      {label && (
        <div ref={labelRef} id={labelId} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </div>
      )}
      {/* Chips hold buttons of their own, so the chevron is the field's keyboard control. */}
      <div
        className={errorMessage ? `${fieldClass} ${styles.fieldError}` : fieldClass}
        onClick={toggleOpen}
      >
        <FieldChips picked={picked} placeholder={placeholder} onRemove={removeChip} />
        <div className={styles.indicators}>
          {errorMessage && (
            <TooltipContainer
              renderReferenceComponent={(className, ref) => (
                <ErrorIcon className={className} ref={ref} />
              )}
            >
              <span>{errorMessage}</span>
            </TooltipContainer>
          )}
          {value.length > 0 && (
            <button type="button" className={styles.indicator} aria-label={t('clear', 'Rensa')} onClick={clearAll}>
              <components.CrossIcon />
            </button>
          )}
          <button
            ref={fieldRef}
            type="button"
            className={styles.indicator}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            {...name}
          >
            <components.DownChevron />
          </button>
        </div>
      </div>
      {open && (
        <Panel
          id={panelId}
          name={name}
          compact={compact}
          searchPlaceholder={searchPlaceholder}
          options={options}
          value={value}
          onChange={onChange}
          matchesQuery={matchesQuery}
          groupsSelectable={groupsSelectable}
          expanded={expanded}
          onToggleExpanded={toggleExpanded}
          searchRef={searchRef}
          showOnly={showOnly}
          onShowOnlyChange={setShowOnly}
          onCreate={onCreate}
          creating={creating}
          onCreatingChange={setCreating}
        />
      )}
    </div>
  );
};

export default BtChecklistSelect;
